import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Sparkles, X, Zap, Clock } from "lucide-react";

const PLAN_LABEL = {
  free: "Free",
  starter: "Starter · $29/mo",
  pro: "Pro · $99/mo",
  enterprise: "Enterprise",
};

/**
 * Global upgrade prompt. Listens for the "peak:upgrade-required" event
 * dispatched by our axios interceptor whenever any backend endpoint returns
 * HTTP 402 (Payment Required). Renders a full-screen modal driving the user
 * to /settings/billing with the specific plan pre-selected.
 */
export default function UpgradeModal() {
  const [payload, setPayload] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handler = useCallback((e) => {
    setPayload(e.detail || {});
  }, []);

  useEffect(() => {
    window.addEventListener("peak:upgrade-required", handler);
    return () => window.removeEventListener("peak:upgrade-required", handler);
  }, [handler]);

  if (!payload) return null;

  const close = () => setPayload(null);
  const goUpgrade = () => {
    setPayload(null);
    navigate("/settings/billing");
  };

  const requiredLabel = PLAN_LABEL[payload.required_plan] || "Paid plan";
  const isLimit = payload.error === "contact_limit_reached";
  const trialEnded = !!user?.trial?.trial_ended;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="upgrade-modal"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close}></div>

      <div className="relative w-full max-w-lg bg-white border-2 border-[var(--peak-ink)]">
        <button
          onClick={close}
          data-testid="upgrade-modal-close"
          className="absolute top-3 right-3 p-1.5 hover:bg-[var(--peak-ink)] hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="bg-[var(--peak-accent)] text-white px-8 py-6">
          <div className="flex items-center gap-2">
            {trialEnded ? <Clock className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
            <span className="text-[10px] font-bold uppercase tracking-widest">
              {trialEnded ? "Trial ended" : isLimit ? "Limit Reached" : "Upgrade required"}
            </span>
          </div>
          <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-2 leading-tight">
            {trialEnded
              ? "Your Pro trial ended."
              : isLimit
              ? "You're out of runway."
              : `Unlock ${requiredLabel}.`}
          </h2>
        </div>

        <div className="px-8 py-6">
          <p className="text-sm text-[var(--peak-ink)] leading-relaxed">
            {trialEnded
              ? "Your 14 days of Pro are up. Pick a plan to keep your workflows, SMS, and AI copilot running — or stay on Free (250 contact cap)."
              : payload.message ||
                `This feature needs the ${requiredLabel} plan. Upgrade now to keep momentum going — cancel anytime.`}
          </p>

          {isLimit && payload.count && payload.limit && (
            <div className="mt-4 border border-[var(--peak-ink)] p-3">
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-[var(--peak-muted)]">
                <span>Contacts used</span>
                <span>
                  {payload.count} / {payload.limit}
                </span>
              </div>
              <div className="mt-2 h-1.5 bg-[var(--peak-border)] relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-[var(--peak-accent)]" style={{ width: "100%" }}></div>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              onClick={goUpgrade}
              data-testid="upgrade-modal-cta"
              className="bg-[var(--peak-accent)] hover:bg-[var(--peak-ink)] text-white text-xs font-bold uppercase tracking-widest px-6 py-3 flex items-center gap-2 transition-colors"
            >
              <Zap className="w-3.5 h-3.5" />
              See plans <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={close}
              className="text-xs font-semibold uppercase tracking-wider text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
